import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import Modal from '../components/shared/Modal';
import EmptyState from '../components/shared/EmptyState';
import { Mail, Plus, Pencil, Trash2, Zap, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../utils/cn';

const INTENT_VALUES = ['activation_problem', 'installation_help', 'download_access', 'order_not_received', 'password_instructions', 'other'];

const defaultForm = {
  name: '', email: '', imap_host: '', imap_port: 993, imap_user: '', imap_password: '',
  smtp_host: '', smtp_port: 465, smtp_user: '', smtp_password: '', smtp_ssl: true,
  escalation_email: '', language: 'nl', is_active: true, autopilot_enabled: false, autopilot_intents: [],
  escalation_smtp_enabled: false, escalation_smtp_host: '', escalation_smtp_port: 465,
  escalation_smtp_user: '', escalation_smtp_password: '', escalation_smtp_ssl: true,
  only_fetch_today: false, review_request_enabled: false,
};

export default function EmailAccounts() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [testResults, setTestResults] = useState({});

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await api.get('/email-accounts');
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setModalOpen(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setForm({
      name: account.name, email: account.email,
      imap_host: account.imap_host, imap_port: account.imap_port,
      imap_user: account.imap_user, imap_password: '',
      smtp_host: account.smtp_host, smtp_port: account.smtp_port,
      smtp_user: account.smtp_user, smtp_password: '',
      smtp_ssl: account.smtp_ssl, escalation_email: account.escalation_email || '',
      language: account.language || 'nl', is_active: account.is_active,
      autopilot_enabled: account.autopilot_enabled, autopilot_intents: account.autopilot_intents || [],
      escalation_smtp_enabled: !!account.escalation_smtp_host,
      escalation_smtp_host: account.escalation_smtp_host || '',
      escalation_smtp_port: account.escalation_smtp_port || 465,
      escalation_smtp_user: account.escalation_smtp_user || '',
      escalation_smtp_password: '',
      escalation_smtp_ssl: account.escalation_smtp_ssl ?? true,
      only_fetch_today: account.only_fetch_today || false,
      review_request_enabled: account.review_request_enabled || false,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form };
      if (editing && !data.imap_password) delete data.imap_password;
      if (editing && !data.smtp_password) delete data.smtp_password;

      if (!data.escalation_smtp_enabled) {
        data.escalation_smtp_host = null;
        data.escalation_smtp_port = null;
        data.escalation_smtp_user = null;
        data.escalation_smtp_password = null;
        data.escalation_smtp_ssl = null;
      } else if (editing && !data.escalation_smtp_password) {
        delete data.escalation_smtp_password;
      }
      delete data.escalation_smtp_enabled;

      if (editing) {
        await api.put(`/email-accounts/${editing.id}`, data);
      } else {
        await api.post('/email-accounts', data);
      }
      setModalOpen(false);
      fetchAccounts();
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleTest = async (id) => {
    setTestResults({ ...testResults, [id]: { loading: true } });
    try {
      const response = await api.post(`/email-accounts/${id}/test`);
      setTestResults({ ...testResults, [id]: response.data });
    } catch (error) {
      setTestResults({ ...testResults, [id]: { imap: false, smtp: false, errors: [t('email_accounts.test_failed')] } });
    }
  };

  const handleFetch = async (id) => {
    try {
      const response = await api.post(`/email-accounts/${id}/fetch-emails`);
      alert(t('email_accounts.tickets_created', { count: response.data.tickets_created }));
    } catch (error) {
      console.error('Fetch failed:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('email_accounts.delete_confirm'))) return;
    try { await api.delete(`/email-accounts/${id}`); fetchAccounts(); } catch (error) { console.error('Delete failed:', error); }
  };

  const toggleIntent = (intent) => {
    const current = form.autopilot_intents || [];
    setForm({
      ...form,
      autopilot_intents: current.includes(intent)
        ? current.filter((i) => i !== intent)
        : [...current, intent],
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('email_accounts.title')}</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
          <Plus className="h-4 w-4" /> {t('email_accounts.add')}
        </button>
      </div>

      {accounts.length === 0 ? (
        <EmptyState icon={Mail} title={t('email_accounts.no_results')} description={t('email_accounts.no_results_hint')} action={<button onClick={openCreate} className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{t('email_accounts.add')}</button>} />
      ) : (
        <div className="space-y-4">
          {accounts.map((account) => (
            <div key={account.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', account.is_active ? 'bg-green-100' : 'bg-gray-100')}>
                    <Mail className={cn('h-5 w-5', account.is_active ? 'text-green-600' : 'text-gray-400')} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{account.name}</h3>
                    <p className="text-sm text-gray-500">{account.email}</p>
                  </div>
                  {account.autopilot_enabled && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      <Zap className="h-3 w-3" /> {t('email_accounts.autopilot')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleTest(account.id)} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors" title={t('email_accounts.test_connection')}>
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleFetch(account.id)} className="p-1.5 text-gray-400 hover:text-green-600 transition-colors" title={t('email_accounts.fetch_emails')}>
                    <Mail className="h-4 w-4" />
                  </button>
                  <button onClick={() => openEdit(account)} className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(account.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                <span>IMAP: {account.imap_host}:{account.imap_port}</span>
                <span>SMTP: {account.smtp_host}:{account.smtp_port}</span>
                {account.last_checked && <span>{t('email_accounts.last_checked')}: {new Date(account.last_checked).toLocaleString()}</span>}
              </div>
              {testResults[account.id] && !testResults[account.id].loading && (
                <div className="mt-3 flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    {testResults[account.id].imap ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />} IMAP
                  </span>
                  <span className="flex items-center gap-1">
                    {testResults[account.id].smtp ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />} SMTP
                  </span>
                  {'escalation_smtp' in testResults[account.id] && (
                    <span className="flex items-center gap-1">
                      {testResults[account.id].escalation_smtp ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />} {t('email_accounts.escalation_smtp_enabled')}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('email_accounts.edit_title') : t('email_accounts.create_title')} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.account_name')} *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')} *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" />
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 pt-2">{t('email_accounts.imap_settings')}</h3>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.host')} *</label><input type="text" value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.port')}</label><input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value) })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.username')} *</label><input type="text" value={form.imap_user} onChange={(e) => setForm({ ...form, imap_user: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.imap_password')} {editing ? t('email_accounts.leave_empty_to_keep') : '*'}</label><input type="password" value={form.imap_password} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} required={!editing} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>

          <h3 className="text-sm font-semibold text-gray-700 pt-2">{t('email_accounts.smtp_settings')}</h3>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.host')} *</label><input type="text" value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.port')}</label><input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value) })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.username')} *</label><input type="text" value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.smtp_password')} {editing ? t('email_accounts.leave_empty_to_keep') : '*'}</label><input type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} required={!editing} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>

          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.smtp_ssl} onChange={(e) => setForm({ ...form, smtp_ssl: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> {t('email_accounts.ssl')}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> {t('email_accounts.active')}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.autopilot_enabled} onChange={(e) => setForm({ ...form, autopilot_enabled: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> {t('email_accounts.autopilot')}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.only_fetch_today} onChange={(e) => setForm({ ...form, only_fetch_today: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> {t('email_accounts.only_fetch_today')}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.review_request_enabled} onChange={(e) => setForm({ ...form, review_request_enabled: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> {t('email_accounts.review_request_enabled')}</label>
          </div>

          {form.autopilot_enabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('email_accounts.autopilot_intents')}</label>
              <div className="flex flex-wrap gap-2">
                {INTENT_VALUES.map((value) => (
                  <button key={value} type="button" onClick={() => toggleIntent(value)} className={cn('px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors', form.autopilot_intents?.includes(value) ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50')}>
                    {t(`intent.${value}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.escalation_email')}</label><input type="email" value={form.escalation_email} onChange={(e) => setForm({ ...form, escalation_email: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.language')}</label><select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"><option value="nl">NL</option><option value="en">EN</option><option value="fr">FR</option><option value="de">DE</option></select></div>
          </div>

          <label className="flex items-center gap-2 text-sm pt-2">
            <input type="checkbox" checked={form.escalation_smtp_enabled} onChange={(e) => setForm({ ...form, escalation_smtp_enabled: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            {t('email_accounts.escalation_smtp_enabled')}
          </label>
          <p className="text-xs text-gray-500 -mt-2">{t('email_accounts.escalation_smtp_hint')}</p>

          {form.escalation_smtp_enabled && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.host')} *</label><input type="text" value={form.escalation_smtp_host} onChange={(e) => setForm({ ...form, escalation_smtp_host: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.port')}</label><input type="number" value={form.escalation_smtp_port} onChange={(e) => setForm({ ...form, escalation_smtp_port: parseInt(e.target.value) })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.username')} *</label><input type="text" value={form.escalation_smtp_user} onChange={(e) => setForm({ ...form, escalation_smtp_user: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('email_accounts.smtp_password')} {editing ? t('email_accounts.leave_empty_to_keep') : '*'}</label><input type="password" value={form.escalation_smtp_password} onChange={(e) => setForm({ ...form, escalation_smtp_password: e.target.value })} required={!editing} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" /></div>
                <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={form.escalation_smtp_ssl} onChange={(e) => setForm({ ...form, escalation_smtp_ssl: e.target.checked })} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" /> {t('email_accounts.ssl')}</label>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{editing ? t('common.update') : t('common.create')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
